/**
 * Money is stored as integer minor units with an ISO 4217 currency (ADR-0007).
 *
 * `amountMinor` is a `bigint` bounded to the signed 64-bit range so a value always persists to a
 * PostgreSQL `BIGINT` column without loss. A JS `number` cannot represent that domain: `BIGINT`'s max
 * (2^63-1 ≈ 9.2e18) is far beyond `Number.MAX_SAFE_INTEGER` (2^53-1 ≈ 9.0e15), where integers silently
 * collapse (e.g. `MAX_SAFE_INTEGER + 1 === MAX_SAFE_INTEGER + 2`), losing minor units.
 */
import { isIso4217Currency } from './iso4217.js';
/** PostgreSQL BIGINT bounds — the persistable minor-unit range. */
export const MAX_MINOR = 9223372036854775807n; // 2^63 - 1
export const MIN_MINOR = -9223372036854775808n; // -2^63
function assertCurrency(currency) {
    // Shape first (a fast, clear failure for obviously wrong input), then the assigned-code allowlist so a
    // well-formed but UNASSIGNED code (e.g. `AAA`) is rejected — not merely `^[A-Z]{3}$`.
    if (!/^[A-Z]{3}$/.test(currency) || !isIso4217Currency(currency)) {
        throw new RangeError(`currency must be an assigned ISO 4217 alpha-3 code: ${currency}`);
    }
}
function assertInRange(amountMinor) {
    if (amountMinor < MIN_MINOR || amountMinor > MAX_MINOR) {
        throw new RangeError(`amountMinor ${amountMinor} is outside the signed 64-bit minor-unit range [${MIN_MINOR}, ${MAX_MINOR}]`);
    }
}
/** Construct Money from an exact minor-unit `bigint`. Rejects non-bigint input and out-of-range values. */
export function money(amountMinor, currency) {
    if (typeof amountMinor !== 'bigint') {
        throw new RangeError('amountMinor must be a bigint number of minor units; use moneyFromSafeInteger for a JS number');
    }
    assertInRange(amountMinor);
    assertCurrency(currency);
    return { amountMinor, currency };
}
/**
 * Construct Money from a JS `number`, rejecting any value that is not a *safe* integer. This is the guarded
 * entry point for numeric input: it rejects `Number.MAX_SAFE_INTEGER + 2` and any fractional value before
 * the silent-precision-loss class of bug can occur.
 */
export function moneyFromSafeInteger(amountMinor, currency) {
    if (!Number.isSafeInteger(amountMinor)) {
        throw new RangeError('amountMinor must be a safe integer (|x| <= Number.MAX_SAFE_INTEGER)');
    }
    return money(BigInt(amountMinor), currency);
}
function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new TypeError(`currency mismatch: ${a.currency} vs ${b.currency}`);
    }
}
/** Sum is computed in arbitrary precision; `money()` then rejects any int64 overflow. */
export function addMoney(a, b) {
    assertSameCurrency(a, b);
    return money(a.amountMinor + b.amountMinor, a.currency);
}
export function subtractMoney(a, b) {
    assertSameCurrency(a, b);
    return money(a.amountMinor - b.amountMinor, a.currency);
}
/** Multiply by an integer factor (e.g. quantity); overflow past the int64 range throws. */
export function multiplyMoney(m, factor) {
    if (typeof factor !== 'bigint') {
        throw new RangeError('factor must be a bigint');
    }
    return money(m.amountMinor * factor, m.currency);
}
export function serializeMoney(m) {
    return { amountMinor: m.amountMinor.toString(), currency: m.currency };
}
/**
 * A wire amount from an already-validated `bigint` minor-unit value and its currency: no validation, no `Money` instance.
 * The route layers use it to put a stored amount on a response (`serializeMoney` takes a `Money` and is the strict form).
 */
export function toMoneyJSON(amountMinor, currency) {
    return { amountMinor: amountMinor.toString(), currency };
}
export function parseMoney(json) {
    if (!/^-?\d+$/.test(json.amountMinor)) {
        throw new RangeError('amountMinor string must be a base-10 integer');
    }
    return money(BigInt(json.amountMinor), json.currency);
}
/**
 * Deep money codec for the store/route boundary — JSONB and the JSON wire cannot hold a `bigint`.
 *
 * A kernel aggregate that *nests* `Money` (a negotiated `PriceSnapshot`, a reservation's held quote, an
 * hourly-package rate table, a pooled fare split) cannot be `JSON.stringify`-d directly: `amountMinor` is a
 * `bigint`. `serializeMoneyDeep` walks the whole value and rewrites every `Money` node
 * (`{ amountMinor: bigint, currency }`) to its `MoneyJSON` (`{ amountMinor: string, currency }`) form;
 * `parseMoneyDeep` is the exact inverse, reviving each base-10 minor-unit string to a real `bigint` `Money`.
 * Detection is by shape — an object whose `amountMinor`/`currency` pair fingerprints Money — so a whole
 * aggregate round-trips through one JSONB column with no hand-written per-field mapper (contrast the M12
 * `decodeQuote`, which names each money field). This is the single seam the M13 Money-bearing kernels share
 * (S118, S120/S121, S125, S129, S130, S131).
 *
 * Money is the *only* `bigint` a Vello kernel produces (ADR-0007), so a bare `bigint` reaching
 * `serializeMoneyDeep` outside a Money node is a bug: it throws rather than silently corrupting the snapshot
 * (a stray bigint→string would never be revived by `parseMoneyDeep`).
 */
function isMoneyShape(v) {
    return typeof v.amountMinor === 'bigint' && typeof v.currency === 'string';
}
function isMoneyJsonShape(v) {
    return (typeof v.amountMinor === 'string' &&
        typeof v.currency === 'string' &&
        /^-?\d+$/.test(v.amountMinor) &&
        /^[A-Z]{3}$/.test(v.currency));
}
export function serializeMoneyDeep(value) {
    if (typeof value === 'bigint') {
        throw new TypeError('serializeMoneyDeep: a bare bigint is not a Money value (money is the only kernel bigint; ADR-0007)');
    }
    if (Array.isArray(value))
        return value.map(serializeMoneyDeep);
    if (value !== null && typeof value === 'object') {
        const v = value;
        if (isMoneyShape(v)) {
            const out = { amountMinor: v.amountMinor.toString(), currency: v.currency };
            for (const [k, val] of Object.entries(v))
                if (k !== 'amountMinor' && k !== 'currency')
                    out[k] = serializeMoneyDeep(val);
            return out;
        }
        const out = {};
        for (const [k, val] of Object.entries(v))
            out[k] = serializeMoneyDeep(val);
        return out;
    }
    return value;
}
export function parseMoneyDeep(value) {
    if (Array.isArray(value))
        return value.map(parseMoneyDeep);
    if (value !== null && typeof value === 'object') {
        const v = value;
        if (isMoneyJsonShape(v)) {
            // Revive through `money()` so an out-of-range or malformed persisted value fails loud, not silently.
            const revived = money(BigInt(v.amountMinor), v.currency);
            const out = { amountMinor: revived.amountMinor, currency: revived.currency };
            for (const [k, val] of Object.entries(v))
                if (k !== 'amountMinor' && k !== 'currency')
                    out[k] = parseMoneyDeep(val);
            return out;
        }
        const out = {};
        for (const [k, val] of Object.entries(v))
            out[k] = parseMoneyDeep(val);
        return out;
    }
    return value;
}
const BIGINT_TAG = '$bigint';
/**
 * Reversible bigint codec for the **store boundary** (a durable JSONB column is opaque — never queried inside).
 *
 * Several kernels carry money as **bare `bigint` minor units** rather than a `Money` object (a negotiated
 * proposal amount and its floor/cap, a cancellation fee, a fare bound), so `serializeMoneyDeep` — which
 * fingerprints `Money` and rejects a bare bigint — does not fit their aggregate. `encodeBigints` walks a value
 * and rewrites **every** `bigint`, wherever it sits, to a tagged marker `{ "$bigint": "<decimal>" }`;
 * `decodeBigints` reverses it exactly, so an aggregate with bare bigints round-trips losslessly through JSONB.
 * Use this at the store boundary; on the HTTP wire prefer the `MoneyJSON` string form clients already decode.
 */
function isBigintTag(v) {
    return typeof v[BIGINT_TAG] === 'string' && Object.keys(v).length === 1 && /^-?\d+$/.test(v[BIGINT_TAG]);
}
export function encodeBigints(value) {
    if (typeof value === 'bigint')
        return { [BIGINT_TAG]: value.toString() };
    if (Array.isArray(value))
        return value.map(encodeBigints);
    if (value !== null && typeof value === 'object') {
        const out = {};
        for (const [k, val] of Object.entries(value))
            out[k] = encodeBigints(val);
        return out;
    }
    return value;
}
export function decodeBigints(value) {
    if (Array.isArray(value))
        return value.map(decodeBigints);
    if (value !== null && typeof value === 'object') {
        const v = value;
        if (isBigintTag(v))
            return BigInt(v[BIGINT_TAG]);
        const out = {};
        for (const [k, val] of Object.entries(v))
            out[k] = decodeBigints(val);
        return out;
    }
    return value;
}
//# sourceMappingURL=money.js.map